import React from "react";
import { Text, TextProps } from "styles/utility-components";

export type UserNameUser = {
  firstName: string;
  lastName: string;
  active: boolean;
};

type UserNameProps = Omit<TextProps, "children" | "color"> & {
  user: UserNameUser | null | undefined;
  fallback?: string;
};

/**
 * Returns "Firstname Lastname" with a " (deactivated)" suffix appended when
 * `active === false`. Use this in places that need a plain string (combobox
 * labels, tooltip text, status banners) — anywhere JSX is supported, prefer
 * `<UserName>`. Both share this helper so the suffix never drifts.
 */
export function formatUserNameWithStatus(user: UserNameUser): string {
  const name = `${user.firstName} ${user.lastName}`.trim();
  return user.active ? name : `${name} (deactivated)`;
}

/**
 * Renders a user's display name, appending a greyed " (deactivated)" suffix
 * when `active === false`. Centralises the rule introduced in APP-4556 so
 * every display surface shows the same suffix and tone.
 *
 * Forwards all `<Text>` props (e.g. fontWeight, bold, ellipsis) to the
 * underlying span, except `color` which is overridden when deactivated.
 */
export default function UserName({ user, fallback = "", ...textProps }: UserNameProps) {
  if (!user) {
    return (
      <Text inline {...textProps}>
        {fallback}
      </Text>
    );
  }

  if (!user.active) {
    return (
      <Text inline {...textProps} color="sysTextInactive">
        {formatUserNameWithStatus(user)}
      </Text>
    );
  }

  return (
    <Text inline {...textProps}>
      {formatUserNameWithStatus(user)}
    </Text>
  );
}
